// Pure helpers shared between the player runtime, the heartbeat function,
// and the Proof of Play unit tests. This file deliberately has no imports
// so node:test can load it via --experimental-strip-types without needing
// node_modules.

export type PlayCompletionStatus = 'completed' | 'partial' | 'interrupted';

export type PlayItemKind = 'image' | 'video' | 'webpage' | 'weather' | 'audio';

export interface PlayEvent {
  clientEventId: string;
  screenId: string;
  revisionId: string | null;
  assetId: string | null;
  itemKind: PlayItemKind | string;
  manifestItemIndex: number;
  manifestItemId: string | null;
  startedAt: string;
  endedAt: string;
  scheduledDurationMs: number;
  actualDurationMs: number;
  completionRatio: number;
  completionStatus: PlayCompletionStatus;
  scheduleState: string | null;
  bundleVersion: string | null;
}

export interface PlayEventStartInput {
  revisionId: string | null;
  assetId: string | null;
  itemKind: PlayItemKind | string;
  manifestItemIndex: number;
  manifestItemId: string | null;
  scheduledDurationMs: number;
}

export interface PlayEventEndInput {
  actualDurationMs: number;
  completionStatus: PlayCompletionStatus;
}

export interface PlayEventHandle {
  clientEventId: string;
  startedAt: number;
  scheduledDurationMs: number;
}

export interface PlayEventContext {
  screenId: string;
  scheduleState: string | null;
  bundleVersion: string | null;
}

/**
 * Build the finalized wire-format event from a start handle, the original
 * start input, and the end disposition. Pure: no IO, no clock reads.
 */
export function buildPlayEvent(
  handle: PlayEventHandle,
  start: PlayEventStartInput,
  end: PlayEventEndInput,
  context: PlayEventContext,
): PlayEvent {
  const scheduled = Math.max(0, handle.scheduledDurationMs);
  const actual = Math.max(0, end.actualDurationMs);
  const endedAtMs = handle.startedAt + actual;
  const ratio = scheduled > 0 ? clamp01(actual / scheduled) : 0;
  return {
    clientEventId: handle.clientEventId,
    screenId: context.screenId,
    revisionId: start.revisionId,
    assetId: start.assetId,
    itemKind: start.itemKind,
    manifestItemIndex: start.manifestItemIndex,
    manifestItemId: start.manifestItemId,
    startedAt: new Date(handle.startedAt).toISOString(),
    endedAt: new Date(endedAtMs).toISOString(),
    scheduledDurationMs: scheduled,
    actualDurationMs: actual,
    completionRatio: Number(ratio.toFixed(4)),
    completionStatus: end.completionStatus,
    scheduleState: context.scheduleState,
    bundleVersion: context.bundleVersion,
  };
}

function clamp01(n: number): number {
  if (!Number.isFinite(n)) return 0;
  if (n < 0) return 0;
  if (n > 1) return 1;
  return n;
}
